import { Guid } from 'guid-typescript';
import { 
    CalculationStatistics,
    ClientLevelCalculationStatistics, 
    ClientLevelImportStatistics, 
    ImportStatistics, 
    JobLevelCalculationStatistics, 
    JobLevelImportStatistics 
} from '../../component/UsageStatistics/UsageStatistics.types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '../../app/store';
import { calculationStatistics, importStatistics } from '../../api/UsageStatistics/UsageStatistics.api';
import { showErrorSnackbar } from '../../component/snackbar/Snackbar.slice';
import { Messages } from '../../Constants/Messages';

interface State {
    jobLevelCalculationStatisticsResponse: JobLevelCalculationStatistics[];
    clientLevelCalculationStatisticsResponse: ClientLevelCalculationStatistics[];
    jobLevelImportStatisticsResponse: JobLevelImportStatistics[];
    clientLevelImportStatisticsResponse: ClientLevelImportStatistics[];
    clientId: string;
    jobId: Guid;
}

const initialState: State = {
    jobLevelCalculationStatisticsResponse: [] as JobLevelCalculationStatistics[],
    clientLevelCalculationStatisticsResponse: [] as ClientLevelCalculationStatistics[],
    jobLevelImportStatisticsResponse: [] as JobLevelImportStatistics[],
    clientLevelImportStatisticsResponse: [] as ClientLevelImportStatistics[],
    clientId: '',
    jobId: Guid.createEmpty()
};

export const loadImportAndCalcStatisticsData = createAsyncThunk<
    void,
    void,
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>('usageStatistics', async (_: void, thunkAPI) => {
    const statisticsTotalDays = 180;
    const endDate: Date = new Date();
    const startDate: Date = new Date();
    let importStatisticsData: ImportStatistics;
    let calculationStatisticsData: CalculationStatistics;
    let errorState = false;
    startDate.setDate(startDate.getDate() - statisticsTotalDays);

    const importStatisticsresponse = async () => {
        return await importStatistics(startDate, endDate);
    };
    const calculationStatisticsresponse = async () => {
        return await calculationStatistics(startDate, endDate);
    };
    return Promise.all([
        importStatisticsresponse(),
        calculationStatisticsresponse()
    ]).then(function (values) {
        if (values.length > 0 && values[0]) {
            importStatisticsData = JSON.parse(values[0].Data.toString()) as ImportStatistics;
        } else {
            if (!errorState) {
                errorState = true;
            }
        }
        if (values.length > 0 && values[1]) {
            calculationStatisticsData = JSON.parse(values[1].Data.toString()) as CalculationStatistics;
        } else {
            if (!errorState) {
                errorState = true;
            }
        }
        if (errorState) {
            showErrorSnackbar(thunkAPI.dispatch, Messages.EXCEPTION_FOR_FETCH);
        } else {
            thunkAPI.dispatch(setClientLevelCalculationStatisticsResponses(calculationStatisticsData.clientLevelCalculationStatisticsResponses));
            thunkAPI.dispatch(setJobLevelCalculationStatisticsResponses(calculationStatisticsData.jobLevelCalculationStatisticsResponses));
            thunkAPI.dispatch(setClientLevelImportStatisticsResponses(importStatisticsData.clientLevelImportStatisticsResponse));
            thunkAPI.dispatch(setJobLevelImportStatisticsResponses(importStatisticsData.jobLevelImportStatisticsResponse));
        }
    });
});

const usageStatisticsSlice = createSlice({
    name: 'usagestatistics',
    initialState,
    reducers: {
        setJobLevelCalculationStatisticsResponses(state, action: PayloadAction<JobLevelCalculationStatistics[]>): any {
            return { ...state, jobLevelCalculationStatisticsResponse: action.payload };
        },
        setClientLevelCalculationStatisticsResponses(state, action:PayloadAction<ClientLevelCalculationStatistics[]>): any {
            return { ...state, clientLevelCalculationStatisticsResponse: action.payload };
        },
        setJobLevelImportStatisticsResponses(state, action: PayloadAction<JobLevelImportStatistics[]>): any {
            return { ...state, jobLevelImportStatisticsResponse: action.payload };
        },
        setClientLevelImportStatisticsResponses(state, action:PayloadAction<ClientLevelImportStatistics[]>): any {
            return { ...state, clientLevelImportStatisticsResponse: action.payload };
        },
        setSelectedClientIdForUsageStatistics(state, action:PayloadAction<string>): any {
            return { ...state, clientId: action.payload };
        },
    },
});

export const { setJobLevelCalculationStatisticsResponses, setClientLevelCalculationStatisticsResponses, 
    setJobLevelImportStatisticsResponses, setClientLevelImportStatisticsResponses,
    setSelectedClientIdForUsageStatistics } = usageStatisticsSlice.actions;

export default usageStatisticsSlice.reducer;